import React, { useEffect, useState } from 'react';
import { investmentTrackingApi, TrackingLedgerInput, MonthlyRecordInput } from '../services/investmentTrackingApi.ts';
import InvestmentIndicators from '../components/InvestmentIndicators.tsx';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import { parseNumberInput, parsePercentInput } from '../utils/numberUtils.ts';
import './InvestmentTracking.css';

interface TrackingLedger {
  id: number;
  name: string;
  currency: string;
}

const InvestmentTrackingPage: React.FC = () => {
  const [ledgers, setLedgers] = useState<TrackingLedger[]>([]);
  const [selectedLedgerId, setSelectedLedgerId] = useState<number | null>(null);
  const [indicators, setIndicators] = useState<any>(null);
  const [inflationInput, setInflationInput] = useState<string>('');
  const [ledgerForm, setLedgerForm] = useState<TrackingLedgerInput>({
    name: '',
    start_month: '',
    currency: 'BRL',
  });
  const [recordForm, setRecordForm] = useState({
    ledger_id: 0,
    reference_month: '',
    opening_balance: '',
    contributions: '',
    withdrawals: '',
    closing_balance: '',
    data_source_note: '',
  });
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  useEffect(() => {
    loadLedgers();
  }, []);

  const loadLedgers = async () => {
    const response = await investmentTrackingApi.listLedgers();
    setLedgers(response.data || []);
  };

  const handleCreateLedger = async (e: React.FormEvent) => {
    e.preventDefault();
    const response = await investmentTrackingApi.createLedger(ledgerForm);
    setLedgers((prev) => [...prev, response.data]);
    setSelectedLedgerId(response.data.id);
  };

  const handleCreateRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLedgerId) return;
    const payload: MonthlyRecordInput = {
      ledger_id: selectedLedgerId,
      reference_month: recordForm.reference_month,
      opening_balance: parseNumberInput(recordForm.opening_balance),
      contributions: parseNumberInput(recordForm.contributions),
      withdrawals: parseNumberInput(recordForm.withdrawals),
      closing_balance: parseNumberInput(recordForm.closing_balance),
      data_source_note: recordForm.data_source_note || undefined,
    };
    await investmentTrackingApi.createRecord(payload);
    await loadIndicators(selectedLedgerId);
  };

  const loadIndicators = async (ledgerId: number) => {
    if (!backendAvailable) return;
    const inflation = inflationInput === '' ? undefined : parsePercentInput(inflationInput);
    const response = await investmentTrackingApi.getIndicators(ledgerId, inflation);
    setIndicators(response.data);
  };

  return (
    <div className="investment-tracking module-name">
      <div className="page-header">
        <div>
          <h1>Tracking de Investimentos</h1>
          <p>Registre movimentações mensais e acompanhe indicadores.</p>
        </div>
      </div>

      <div className="module-content">
        {!backendAvailable && (
          <div className="message error" style={{ marginBottom: '16px' }}>
            ⚠️ Indicadores exigem conexão com o backend. Cadastros locais continuam disponíveis.
          </div>
        )}
        <form className="form-container tracking-form" onSubmit={handleCreateLedger}>
          <h3>Novo ledger</h3>
          <div className="form-group">
            <label>Nome</label>
            <input
              type="text"
              value={ledgerForm.name}
              onChange={(e) => setLedgerForm({ ...ledgerForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Mês inicial</label>
              <input
                type="date"
                value={ledgerForm.start_month}
                onChange={(e) => setLedgerForm({ ...ledgerForm, start_month: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Moeda</label>
              <input
                type="text"
                value={ledgerForm.currency}
                onChange={(e) => setLedgerForm({ ...ledgerForm, currency: e.target.value })}
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="primary-button" type="submit">Salvar ledger</button>
          </div>
        </form>

        <div className="tracking-selection">
          <h3>Selecionar ledger</h3>
          <select
            value={selectedLedgerId ?? ''}
            onChange={(e) => {
              const value = Number(e.target.value);
              setSelectedLedgerId(value);
              if (value) {
                loadIndicators(value);
              }
            }}
          >
            <option value="">Selecione</option>
            {ledgers.map((ledger) => (
              <option key={ledger.id} value={ledger.id}>
                {ledger.name}
              </option>
            ))}
          </select>
          <div className="form-group">
            <label>Inflação anual (opcional)</label>
            <input
              type="text"
              value={inflationInput}
              onChange={(e) => setInflationInput(e.target.value)}
            />
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => selectedLedgerId && loadIndicators(selectedLedgerId)}
            disabled={!selectedLedgerId || !backendAvailable}
          >
            Atualizar indicadores
          </button>
        </div>

        <form className="form-container tracking-form" onSubmit={handleCreateRecord}>
          <h3>Novo registro mensal</h3>
          <div className="form-group">
            <label>Mês de referência</label>
            <input
              type="date"
              value={recordForm.reference_month}
              onChange={(e) => setRecordForm({ ...recordForm, reference_month: e.target.value })}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Saldo inicial</label>
              <input
              type="text"
                value={recordForm.opening_balance}
              onChange={(e) => setRecordForm({ ...recordForm, opening_balance: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Saldo final</label>
              <input
              type="text"
                value={recordForm.closing_balance}
              onChange={(e) => setRecordForm({ ...recordForm, closing_balance: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Contribuições</label>
              <input
              type="text"
                value={recordForm.contributions}
              onChange={(e) => setRecordForm({ ...recordForm, contributions: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Retiradas</label>
              <input
              type="text"
                value={recordForm.withdrawals}
              onChange={(e) => setRecordForm({ ...recordForm, withdrawals: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Observações</label>
            <input
              type="text"
              value={recordForm.data_source_note}
              onChange={(e) => setRecordForm({ ...recordForm, data_source_note: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={!selectedLedgerId}>
              Salvar registro
            </button>
          </div>
        </form>

        <InvestmentIndicators indicators={indicators} />
      </div>
    </div>
  );
};

export default InvestmentTrackingPage;
